import { useState } from 'react';
import { Message } from '@/types/chat.types';
import { chatService } from '@/services/chatService';

interface UseChatOptions {
  onMessageReceived?: () => void;
}

export const useChat = (options?: UseChatOptions) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatService.sendMessage(content, currentSessionId || undefined);

      // Update session ID if backend returned one (important for new sessions)
      if (response.sessionId && response.sessionId !== currentSessionId) {
        setCurrentSessionId(response.sessionId);
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: response.messageId || `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.reply,
        timestamp: response.timestamp ? new Date(response.timestamp) : new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Notify that a message was received (for balance refresh, etc.)
      if (options?.onMessageReceived) {
        options.onMessageReceived();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      console.error('Error sending message:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
    setError(null);
  };

  const startNewSession = async () => {
    // Generate a new session ID locally without creating anything in the database
    // The session will be created when the first message is sent
    const sessionId = crypto.randomUUID();
    setCurrentSessionId(sessionId);
    setMessages([]);
    setError(null);
    return sessionId;
  };

  const loadSession = async (sessionId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatService.getSessionMessages(sessionId);

      const loadedMessages: Message[] = response.messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
      }));

      setMessages(loadedMessages);
      setCurrentSessionId(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load session';
      setError(errorMessage);
      console.error('Error loading session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    isLoading,
    error,
    currentSessionId,
    sendMessage,
    clearMessages,
    startNewSession,
    loadSession,
  };
};
